import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useAppContext } from "../../../../../../../context/AppContext";
import CardLoader from "../../../../../../../components/CardLoader";
import { encodeId } from "../../../../../../../utils/hash";
import { ProductD } from "../network/api/ProductD";
import Toast from "../../../../../../../components/Toast";
import { useNavigate } from "react-router-dom";
import PayPal from "./gateways/components/PayPal";

export default function ProductDetails({ slug, loggedInUserData, lockedQuantity = null }) {
    const { appData } = useAppContext();
    const [product, setProduct] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [pageErrorMessage, setPageErrorMessage] = useState(null);
    const [reload, setReload] = useState(false);
    const navigate = useNavigate();

    const [toastConfig, setToastConfig] = useState({ visible: false, message: "", type: "primary" });
    // Quantity and selectedSize state for controlled inputs
    const [minQuantity, setMinQuantity] = useState(1);
    const [quantity, setQuantity] = useState(lockedQuantity !== null ? lockedQuantity : minQuantity);
    const [selectedSize, setSelectedSize] = useState(null);

    const [successPage, setSuccessPage] = useState(false);
    const orderMetadata = {
        ...(loggedInUserData?.metadata || {}),
        ...(selectedSize ? { size: selectedSize } : {}),
    };



    useEffect(() => {
        const fetchProduct = async () => {
            setIsLoading(true);
            try {
                const response = await ProductD(appData.id, slug);

                if (response.status === "success") {
                    const productData = response.data;
                    //console.log("Product Data:", productData);
                    if (productData.metadata && typeof productData.metadata === "string") {
                        try {
                            productData.metadata = JSON.parse(productData.metadata);
                        } catch (e) {
                            productData.metadata = {};
                        }
                    }
                    setProduct(productData);
                    if (productData?.merchant_gateways?.length === 1) {
                        setSelectedGateway(productData.merchant_gateways[0]);
                    }
                    //set min quantity based on product metadata if available
                    if (productData.metadata && productData.metadata.min_quantity) {
                        const minQty = parseInt(productData.metadata.min_quantity, 10);
                        if (!isNaN(minQty) && minQty > 0) {
                            setMinQuantity(minQty);
                            if (lockedQuantity === null)
                                setQuantity(minQty);
                        }
                    }
                } else {
                    setPageErrorMessage({ code: response.code, message: response.message });
                }
            } catch (err) {
                setPageErrorMessage({ code: 500, message: "Failed to get Product." });
            } finally {
                setIsLoading(false);
            }
        };

        fetchProduct();
    }, [appData.id, slug, lockedQuantity]);


    const [selectedGateway, setSelectedGateway] = useState(null);


    return (
        <div className="col-lg-12">
            <Toast message={toastConfig.message}
                type={toastConfig.type}
                visible={toastConfig.visible}
                setVisible={(visible) => setToastConfig({ ...toastConfig, visible })} />
            <div className="card custom-card border-0 shadow-sm rounded-4">
                {(isLoading || pageErrorMessage) && (<CardLoader errorMessage={pageErrorMessage} onRetry={() => { setReload(!reload); }} />)}

                <div className="card-body p-3 p-lg-4">
                    <div className="row g-4">

                        <div className="col-xl-5 col-lg-6 col-md-6 col-xxl-5">
                            <div className=" rounded-4 ">
                                <img
                                    src={product?.image ? product?.image : "https://www.shutterstock.com/image-vector/default-ui-image-placeholder-wireframes-600nw-1037719192.jpg"}
                                    alt={product?.name}
                                    className="img-fluid rounded-4 shadow-sm"
                                    style={{ width: "100%", objectFit: "cover" }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "https://www.shutterstock.com/image-vector/default-ui-image-placeholder-wireframes-600nw-1037719192.jpg";
                                    }}
                                />
                            </div>
                        </div>

                        <div className="col-xl-7 col-lg-6 col-md-6 col-xxl-7 col-sm-12">
                            <div className="p-4 rounded-4 border  shadow-sm h-100 d-flex flex-column">
                                <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                                    <div>
                                        <h4 className="mb-1 fw-bold text-body-emphasis">{product?.name}</h4>
                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                            {product?.metadata?.featured && <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis border border-primary-subtle">Featured</span>}
                                            <span className="badge rounded-pill text-body border">Product code: {encodeId(product?.id)}</span>
                                            <span className="badge rounded-pill text-body border">Type: {product?.type}</span>
                                        </div>
                                    </div>
                                    <div className="text-end">
                                        <div className="small text-body-secondary">Price</div>
                                        <div className="fs-3 fw-bold text-primary">${(product?.price * quantity)}</div>
                                        {product?.metadata?.cancelled_price && (
                                            <div className="text-body-secondary text-decoration-line-through">${(product?.metadata?.cancelled_price * quantity)}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="d-flex align-items-center justify-content-between p-3 rounded-3 mb-3 border">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge rounded-pill bg-success">Available</span>
                                        <span className="text-body-secondary">Ready for checkout</span>
                                    </div>
                                    <span className="text-success fw-semibold">
                                        <i className="fa fa-check-circle me-2"></i>
                                        In stock
                                    </span>
                                </div>

                                <div className="mb-2 text-body-emphasis">Payment: <span className="item">{product?.payment_type}</span></div>
                                {product?.duration && (
                                    <div className="mb-2 text-body-emphasis">Duration: <span className="item">{product.duration > 3000 ? "Lifetime" : `${product.duration} days`}</span></div>
                                )}

                                {product?.metadata?.tags && product.metadata.tags.length > 0 && (
                                    <div className="mb-3">
                                        Product tags:&nbsp;&nbsp;
                                        {product.metadata.tags.map((tag, index) => (
                                            <span key={index} className="badge rounded-pill  text-info border border-info me-1">{tag}</span>
                                        ))}
                                    </div>
                                )}

                                <p className="text-body-secondary mb-4">{product?.description}</p>

                                {product?.metadata?.sizes && (
                                    <div className="mb-4">
                                        <h5 className="fw-semibold mb-2">Select size</h5>
                                        <div className="btn-group flex-wrap gap-2" role="group">
                                            {product.metadata.sizes.map((size, index) => (
                                                <React.Fragment key={index}>
                                                    <input
                                                        type="radio"
                                                        className="btn-check"
                                                        name="btnradio"
                                                        id={`btnradio${index}`}
                                                        checked={selectedSize === size}
                                                        onChange={() => setSelectedSize(size)}
                                                    />
                                                    <label className="btn btn-outline-primary mb-0" htmlFor={`btnradio${index}`}>
                                                        {size}
                                                    </label>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {successPage ? (
                                    <motion.div
                                        className="d-flex justify-content-center align-items-center flex-column text-center py-5  border rounded-4"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.6, ease: "easeOut" }}
                                    >
                                        <div className="mb-4">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" fill="currentColor" className="bi bi-check-circle-fill text-success" viewBox="0 0 16 16">
                                                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.97 11.03a.75.75 0 0 0 1.08.022l4.992-4.99a.75.75 0 1 0-1.06-1.06L7.5 9.439 5.02 6.97a.75.75 0 1 0-1.04 1.08l2.99 2.98z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-success fw-bold">Payment Successful</h3>
                                        <p className="text-body-secondary fs-6 mb-4 px-4" style={{ maxWidth: "400px" }}>
                                            Thank you for your purchase! Your transaction was completed successfully.
                                            An email confirmation with your order details has been sent.
                                        </p>
                                        <button className="btn btn-outline-primary" onClick={() => navigate("/shop")}>
                                            Continue Shopping
                                        </button>
                                    </motion.div>
                                ) : (
                                    <>
                                        <div className="p-4 rounded-4 mt-4  border">
                                            <h4 className="m-b-15 fw-bold mb-3 text-body-emphasis">Select Purchase Options</h4>
                                            <div className="row gx-3 mx-0">
                                                <div className="col-12 col-sm-5 col-md-4 px-0 pe-sm-3 mb-2 mb-sm-0">
                                                    <label className="form-label small text-body-secondary">Quantity (minimum {minQuantity})</label>
                                                    <input
                                                        type="number"
                                                        name="num"
                                                        className="form-control form-control-sm input-number"
                                                        value={lockedQuantity !== null ? lockedQuantity : (quantity === null ? "" : quantity)}
                                                        min={minQuantity}
                                                        placeholder="Quantity"
                                                        onChange={(e) => {
                                                            if (lockedQuantity !== null) return;
                                                            const val = e.target.value;
                                                            if (val === "") {
                                                                setQuantity(null);
                                                            } else {
                                                                const num = parseInt(val, 10);
                                                                if (!isNaN(num) && num > 0) {
                                                                    setQuantity(num);
                                                                }
                                                            }
                                                        }}
                                                        onBlur={(e)=>{
                                                            // Ensure quantity is not left empty or below minimum on blur
                                                            if (lockedQuantity !== null) return;
                                                            const val = e.target.value;
                                                            if (val === "" || parseInt(val, 10) < minQuantity) {
                                                                setQuantity(minQuantity);
                                                            }
                                                        }}
                                                        disabled={lockedQuantity !== null}
                                                    />
                                                </div>
                                                <div className="col-12 px-0 col-sm-7 col-md-8 col-lg-6 position-relative">
                                                    <label className="form-label small text-body-secondary">Payment Method</label>
                                                    <div className="dropdown w-100">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-primary btn-sm dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                                                            data-bs-toggle="dropdown"
                                                            aria-expanded="false"
                                                            disabled={!product?.merchant_gateways || product?.merchant_gateways.length === 0}
                                                        >
                                                            <span>
                                                                <i className="fa fa-credit-card me-2"></i>
                                                                {selectedGateway ? selectedGateway.gateway.toUpperCase() : "Choose Payment Gateway"}
                                                            </span>
                                                        </button>

                                                        <div className="dropdown-menu w-100 shadow-sm border-0" style={{ zIndex: 1050 }}>
                                                            <span className="dropdown-item py-2" onClick={() => setSelectedGateway(null)}>
                                                                Choose Payment Gateway
                                                            </span>
                                                            <div className="dropdown-divider"></div>
                                                            {product?.merchant_gateways?.map((gateway) => (
                                                                <button
                                                                    className={`dropdown-item btn btn-outline-primary text-start py-2 ${selectedGateway === gateway.gateway ? "active" : ""}`}
                                                                    key={gateway.gateway}
                                                                    onClick={() => setSelectedGateway(gateway)}
                                                                >
                                                                    <i className="fa fa-credit-card me-2"></i>
                                                                    {gateway.gateway.toUpperCase()}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-4">
                                            {(selectedGateway?.gateway === "paypal" && quantity >= minQuantity) && (
                                                <PayPal
                                                    credentialsPublic={selectedGateway?.credentials_public}
                                                    createOrderPayload={{
                                                        app_id: appData?.id,
                                                        product_id: product?.id,
                                                        merchant_gateway_id: selectedGateway?.id,
                                                        user_id: loggedInUserData?.user?.userId || null,
                                                        guest_email: loggedInUserData?.user?.email || null,
                                                        quantity,
                                                        metadata: Object.keys(orderMetadata).length > 0 ? orderMetadata : null,
                                                    }}
                                                    setToastConfig={setToastConfig}
                                                    setSuccessPage={setSuccessPage}
                                                />
                                            ) }
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
